import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Divider, Chip } from "@mui/material";

import axios from "axios";
import Swal from "sweetalert2";

const defaultData = {
    license_key: "",
    expiry_date: "2050-12-31",
    company_name: "",
    contact: "",
    mac_address: "",
};

const defaultErrors = {
    license_key: false,
    expiry_date: false,
    company_name: false,
    contact: false,
    mac_address: false,
};

export default function CreateUtilityLicenseForm({ open, handleClose, fetchData }) {
    const [newData, setNewData] = useState(defaultData);
    const [errors, setErrors] = useState(defaultErrors);
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const clearError = async (field) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
    };

    const validate = async (field, value) => {
        if (!value || value === "") {
            await setError(field, "This field is required.");
            return;
        }

        if (field === "expiry_date" && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            await setError("expiry_date", "Expiry Date must be in YYYY-MM-DD format.");
            return;
        }

        await clearError(field);
    };

    useEffect(() => {
        if (open) {
            setNewData(defaultData);
            setErrors(defaultErrors);
            setHelperText({});
            setErrorMessage("");
        }
    }, [open]);

    const handleCreateUtilityLicense = async () => {
        let isValid = true;

        for (const [field, value] of Object.entries(newData)) {
            await validate(field, value);
            if (!value) {
                isValid = false;
            }
        }

        if (!/^\d{4}-\d{2}-\d{2}$/.test(newData.expiry_date)) {
            isValid = false;
        }

        if (!isValid) {
            setErrorMessage("Please fill all the required fields correctly.");
            return;
        }

        setErrorMessage("");

        try {
            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/utility-license/create", {
                ...newData,
            });

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Utility license created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the utility license. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Utility License</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Utility License Details" />
                </Divider>
                <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    id="license_key"
                    label="License Key"
                    type="text"
                    fullWidth
                    required
                    value={newData.license_key}
                    error={errors.license_key}
                    helperText={helperText.license_key}
                    onChange={(e) => {
                        const value = e.target.value || "";
                        setNewData({ ...newData, license_key: value });
                        validate("license_key", value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="expiry_date"
                    label="Expiry Date"
                    type="date"
                    fullWidth
                    required
                    value={newData.expiry_date}
                    error={errors.expiry_date}
                    helperText={helperText.expiry_date}
                    onChange={(e) => {
                        setNewData({ ...newData, expiry_date: e.target.value });
                        validate("expiry_date", e.target.value);
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="company_name"
                    label="Company Name"
                    type="text"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    value={newData.company_name}
                    error={errors.company_name}
                    helperText={helperText.company_name}
                    onChange={(e) => {
                        const value = e.target.value || "";
                        setNewData({ ...newData, company_name: value });
                        validate("company_name", value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="contact"
                    label="Contact"
                    type="text"
                    fullWidth
                    required
                    value={newData.contact}
                    error={errors.contact}
                    helperText={helperText.contact}
                    onChange={(e) => {
                        setNewData({ ...newData, contact: e.target.value || "" });
                        validate("contact", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="mac_address"
                    label="Mac Address"
                    type="text"
                    fullWidth
                    required
                    value={newData.mac_address}
                    error={errors.mac_address}
                    helperText={helperText.mac_address}
                    onChange={(e) => {
                        const value = e.target.value || "";
                        setNewData({ ...newData, mac_address: value });
                        validate("mac_address", value);
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreateUtilityLicense} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Create
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
